// Minimal long-press action for Svelte. Fires `onlong` after `delay` ms unless
// the pointer moves > moveTolerance px or releases first. Right-click is
// treated as long-press on desktop. Cancelled long-presses fall through and
// the caller's normal click handler fires.

export type LongPressOpts = {
  onlong: (ev: PointerEvent | MouseEvent) => void;
  delay?: number;
  moveTolerance?: number;
};

export function longpress(node: HTMLElement, opts: LongPressOpts) {
  let timer: number | undefined;
  let startX = 0;
  let startY = 0;
  let firedLong = false;
  const delay = opts.delay ?? 500;
  const tol = opts.moveTolerance ?? 10;

  function clear() {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  }

  function down(e: PointerEvent) {
    if (e.button === 2) return; // contextmenu handles right-click
    firedLong = false;
    startX = e.clientX;
    startY = e.clientY;
    clear();
    timer = window.setTimeout(() => {
      firedLong = true;
      timer = undefined;
      opts.onlong(e);
    }, delay);
  }

  function move(e: PointerEvent) {
    if (timer === undefined) return;
    if (Math.hypot(e.clientX - startX, e.clientY - startY) > tol) clear();
  }

  function up() {
    clear();
  }

  function leave() {
    clear();
  }

  function ctx(e: MouseEvent) {
    e.preventDefault();
    firedLong = true;
    clear();
    opts.onlong(e);
  }

  // Suppress the synthetic click after a long-press fired.
  function click(e: MouseEvent) {
    if (firedLong) {
      firedLong = false;
      e.stopPropagation();
      e.preventDefault();
    }
  }

  node.addEventListener("pointerdown", down);
  node.addEventListener("pointermove", move);
  node.addEventListener("pointerup", up);
  node.addEventListener("pointercancel", up);
  node.addEventListener("pointerleave", leave);
  node.addEventListener("contextmenu", ctx);
  node.addEventListener("click", click, true);

  return {
    update(next: LongPressOpts) {
      opts = next;
    },
    destroy() {
      clear();
      node.removeEventListener("pointerdown", down);
      node.removeEventListener("pointermove", move);
      node.removeEventListener("pointerup", up);
      node.removeEventListener("pointercancel", up);
      node.removeEventListener("pointerleave", leave);
      node.removeEventListener("contextmenu", ctx);
      node.removeEventListener("click", click, true);
    },
  };
}
